/*
  Project Structure tab pieces.
  Wave 1 (screen 08):
  - StructureSummaryStrip — Buildings · Floors · Rooms · Panels · Devices + device mix (idea 7)
  - StructureCountChip    — "3 fl · 46" on every row instead of the "?" icon; hover/focus shows the breakdown (idea 2)
  - StructureRowActions   — "+" (add child) and "⋯" (rename / duplicate / delete…) that appear on row hover (idea 3)
  Wave 2 (screen 08):
  - StructureName         — names are text; Rename (button / menu) or a double-click edits them in place (idea 9)
  - StructureDeviceCells  — a device's part no · name · address as text; "Edit device" / double-click edits (idea 9)
  - StructureSearch       — "Find device, address or part no." with the full path of every match (idea 5)
*/
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Building2, Check, ChevronRight, Cpu, DoorOpen, Layers, ListChecks, Pencil, Plus, SearchX, Server, SquareDashed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RowActions } from '@/components/ui/row-actions';
import { SearchField } from '@/components/ui/search-field';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ToneDot } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { ActionMenu, CountChip, MONO, MiniStat, Panel } from './ProjectUi';
import { describeChange, searchStructureIndex } from './structureModel';

const MIX_COLORS = {
  push: 'var(--brand)',
  touch: 'var(--status-blue)',
  actuator: 'var(--status-teal, #0f766e)',
  sensor: 'var(--status-amber)',
  gateway: 'var(--status-violet)',
  other: 'var(--status-gray)',
};

export function StructureSummaryStrip({ summary, isBms, conflicts = null }) {
  const stats = [
    { key: 'buildings', label: 'Buildings', value: summary.buildings },
    { key: 'floors', label: isBms ? 'Floors / subs' : 'Floors', value: summary.floors },
    { key: 'rooms', label: isBms ? 'Zones' : 'Rooms', value: summary.rooms },
    { key: 'panels', label: 'Panels', value: summary.panels + summary.zones, meta: summary.zones ? `${summary.zones} zones` : null },
    { key: 'devices', label: 'Devices', value: summary.devices },
    // wave 3 (08 #6): addresses used more than once (KNX / LCS)
    conflicts !== null && conflicts !== undefined ? { key: 'conflicts', label: 'Address conflicts', value: conflicts, tone: conflicts ? 'red' : null } : null,
  ].filter(Boolean);
  const mixTotal = summary.mix.reduce((sum, entry) => sum + entry.count, 0);
  return (
    <Panel className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 sm:px-5" data-testid="structure-summary">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {stats.map((stat, index) => (
          <MiniStat
            key={stat.key}
            label={stat.label}
            value={formatNumber(stat.value, { decimals: 0 })}
            className={cn(index > 0 && 'border-l border-line pl-5')}
            valueClassName={stat.tone === 'red' ? 'text-status-red-ink' : undefined}
          />
        ))}
      </div>
      {mixTotal > 0 ? (
        <div className="ml-auto flex min-w-[260px] flex-1 flex-col gap-1.5 sm:max-w-[520px]">
          <div className="flex h-2 w-full gap-[2px] overflow-hidden rounded-full bg-[color:var(--status-gray-bg)]" role="img" aria-label={`Device mix: ${summary.mix.map((entry) => `${entry.count} ${entry.label}`).join(', ')}`}>
            {summary.mix.map((entry) => (
              <span key={entry.key} className="h-full" style={{ width: `${(entry.count / mixTotal) * 100}%`, background: MIX_COLORS[entry.key] || MIX_COLORS.other }} />
            ))}
          </div>
          <p className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink-3">
            <span className="font-semibold text-ink-2">Mix:</span>
            {summary.mix.map((entry) => (
              <span key={entry.key} className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: MIX_COLORS[entry.key] || MIX_COLORS.other }} aria-hidden="true" />
                <span className="font-semibold tabular-nums text-ink-2">{formatNumber(entry.count, { decimals: 0 })}</span>
                {entry.label}
              </span>
            ))}
          </p>
        </div>
      ) : (
        <p className="ml-auto text-[12.5px] text-ink-3">No devices yet — add devices to floors, rooms or panels.</p>
      )}
    </Panel>
  );
}

/**
 * "3 fl · 46" chip; the device breakdown shows on hover / keyboard focus.
 * `childNoun` names what `floors` counts when it is not floors (BMS buildings hold subs: "2 subs · 728").
 * `interactive={false}`: plain text, never a tab stop — for rows that are focusable themselves (the tree's
 * treeitems, whose name already says what is counted; 08 #2 focus fix).
 */
export function StructureCountChip({ label, floors, devices, tooltip, testId, childNoun = 'floor', interactive = true }) {
  const hasChildren = floors !== undefined && floors !== null;
  const plural = (count, noun) => `${noun}${count === 1 ? '' : 's'}`;
  const childText = childNoun === 'floor' ? 'fl' : plural(floors, childNoun);
  const text = hasChildren
    ? `${formatNumber(floors, { decimals: 0 })} ${childText} · ${formatNumber(devices, { decimals: 0 })}`
    : formatNumber(devices, { decimals: 0 });
  const aria = `${label}: ${hasChildren ? `${floors} ${plural(floors, childNoun)}, ` : ''}${devices} ${plural(devices, 'device')}. Show device totals`;
  if (!interactive) {
    return (
      <CountChip data-testid={testId} title={`${hasChildren ? `${floors} ${plural(floors, childNoun)} · ` : ''}${formatNumber(devices, { decimals: 0 })} ${plural(devices, 'device')}`}>
        {text}
      </CountChip>
    );
  }
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label={aria} className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={testId}>
            <CountChip>{text}</CountChip>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="w-auto max-w-[min(96vw,80rem)] border border-zinc-700 bg-zinc-900 text-zinc-100">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Row actions that stay out of the way: hidden until the row is hovered or focused on pointer devices,
 * always visible on touch screens. Put `group/row` on the row container.
 */
export function StructureRowActions({ label, addItems = [], menuItems = [], onRename, className, testId, compact = false }) {
  const adds = addItems.filter(Boolean);
  const menu = menuItems.filter(Boolean);
  if (!adds.length && !menu.length && !onRename) return null;
  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-1 transition-opacity focus-within:opacity-100 has-[[data-state=open]]:opacity-100',
        '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/row:opacity-100',
        className
      )}
      data-testid={testId}
    >
      {onRename ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRename}
          aria-label={`Rename ${label}`}
          title="Rename (or double-click the name)"
          className={cn('h-[30px] px-2 text-ink-2 max-sm:w-[30px] max-sm:px-0', compact && 'w-[28px] px-0')}
        >
          <Pencil aria-hidden="true" />
          {compact ? null : <span className="max-sm:hidden">Rename</span>}
        </Button>
      ) : null}
      {adds.length ? (
        <ActionMenu
          label={`Add to ${label}`}
          align="end"
          trigger={(
            <Button type="button" variant="outline" size="icon-sm" aria-label={`Add to ${label}`} className="data-[state=open]:bg-surface-hover">
              <Plus aria-hidden="true" />
            </Button>
          )}
          items={adds}
        />
      ) : null}
      {menu.length ? <RowActions label={`Actions for ${label}`} items={menu} size="default" className="h-[30px] w-[30px]" /> : null}
    </div>
  );
}

/** Chevron that opens / closes a tree row. */
export function StructureToggle({ collapsed, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={!collapsed}
      aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${label}`}
      className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-ink-3 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ChevronRight className={cn('h-4 w-4 transition-transform', !collapsed && 'rotate-90')} aria-hidden="true" />
    </button>
  );
}

/** Small kind icon in front of a tree row's name. */
export function StructureKindIcon({ kind, className }) {
  const Icon = KIND_ICONS[kind] || Cpu;
  return <Icon className={cn('h-4 w-4 shrink-0 text-ink-3', className)} aria-hidden="true" />;
}

/* ------------------------------------------------------------------ read mode (idea 9) */

/**
 * A structure name as text. While `editing` it is an input: Enter or leaving the field keeps the new name,
 * Escape puts the old one back; an empty name is never saved. A double-click starts editing (canEdit).
 * The input carries data-structure-name={nodeId} (older code focuses it by that).
 */
export function StructureName({
  value,
  nodeId,
  kind = 'name',
  editing = false,
  canEdit = true,
  onStartEdit,
  onCommit,
  onCancel,
  className,
  inputClassName,
  fieldClassName, // the field's wrapper while editing (e.g. flex-1 in a tree row)
  placeholder = 'Untitled',
  // BUG-010: e.g. "Building name is required" — Enter on an empty name then says so in the field (red, announced)
  // and keeps editing, instead of quietly keeping the old name. Esc or leaving the field still keeps the old name.
  requiredMessage = '',
}) {
  const [draft, setDraft] = useState(value || '');
  const [error, setError] = useState('');
  const doneRef = useRef(false);
  const inputRef = useRef(null);
  const errorId = useId();
  useEffect(() => {
    if (!editing) return;
    setDraft(value || '');
    setError('');
    doneRef.current = false;
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frame);
    // Only when editing starts: the draft must not be reset while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  if (editing) {
    const finish = (keep, { enter = false } = {}) => {
      if (doneRef.current) return;
      const next = draft.trim();
      if (keep && enter && !next && requiredMessage) {
        setError(requiredMessage);
        return;
      }
      doneRef.current = true;
      if (keep && next && next !== String(value || '').trim()) onCommit?.(next);
      else onCancel?.();
    };
    return (
      // The message is a small bubble under the field, so the tree row keeps its height and nothing moves.
      <span className={cn('relative flex min-w-0 max-w-full', fieldClassName)}>
        <Input
          ref={inputRef}
          value={draft}
          onChange={(event) => { setDraft(event.target.value); if (error) setError(''); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); finish(true, { enter: true }); }
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); finish(false); }
          }}
          onBlur={() => finish(true)}
          aria-label={`${kind} name`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          data-structure-name={nodeId}
          className={cn(
            'h-8 w-64 max-w-full rounded-lg text-[13.5px]',
            error && 'border-status-red focus-visible:ring-status-red/40',
            inputClassName
          )}
        />
        {error ? (
          <span
            id={errorId}
            role="alert"
            className="absolute left-0 top-full z-20 mt-1 whitespace-nowrap rounded-md border border-status-red/30 bg-status-red-bg px-2 py-1 text-[12px] font-semibold text-status-red-ink shadow-sm"
            data-testid="structure-name-error"
          >
            {error}
          </span>
        ) : null}
      </span>
    );
  }
  return (
    <span
      className={cn('min-w-0 truncate text-[13.5px] font-semibold text-ink', canEdit && 'cursor-text', className)}
      onDoubleClick={canEdit ? onStartEdit : undefined}
      title={canEdit ? 'Double-click to rename' : undefined}
      data-structure-name-text={nodeId}
    >
      {String(value || '').trim() || <span className="font-normal text-ink-3">{placeholder}</span>}
    </span>
  );
}

const DEVICE_FIELDS = [
  { key: 'part_number', label: 'Part number', mono: true },
  { key: 'name', label: 'Device name' },
  { key: 'address', label: 'Device address', mono: true },
];

/**
 * Part number · device name · address of one device (three grid cells). Text until `editing`, then three
 * inputs that change the structure as you type (like before); Enter / Escape / leaving the row ends editing.
 * Double-click a value to edit the device with that field focused.
 */
export function StructureDeviceCells({ device, editing, canEdit = true, focusField, onStartEdit, onChange, onDone, duplicateAddress = false }) {
  const groupRef = useRef(null);
  useEffect(() => {
    if (!editing) return;
    const frame = window.requestAnimationFrame(() => {
      const field = groupRef.current?.querySelector(`[data-device-field="${focusField || 'part_number'}"]`);
      field?.focus();
      field?.select?.();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [editing, focusField]);

  if (editing) {
    return (
      <div
        ref={groupRef}
        className="contents"
        onBlur={(event) => {
          if (!event.currentTarget.parentElement?.contains(event.relatedTarget)) onDone?.();
        }}
      >
        {DEVICE_FIELDS.map((field) => (
          <Input
            key={field.key}
            value={device[field.key] || ''}
            onChange={(event) => onChange(field.key, event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === 'Escape') { event.preventDefault(); onDone?.(); }
            }}
            placeholder={field.label}
            aria-label={`${field.label} of ${device.name || device.part_number || 'device'}`}
            data-device-field={field.key}
            data-device-cell={field.key}
            className={cn('h-8 rounded-lg text-[13px]', field.mono && MONO)}
          />
        ))}
      </div>
    );
  }
  return (
    <>
      {DEVICE_FIELDS.map((field) => {
        const value = String(device[field.key] || '').trim();
        const isDuplicate = field.key === 'address' && duplicateAddress && value;
        return (
          <span
            key={field.key}
            className={cn(
              'flex h-8 min-w-0 items-center truncate rounded-md px-1 text-[13px]',
              field.key === 'name' ? 'text-ink' : 'text-ink-2',
              field.mono && MONO,
              field.mono && 'text-[12.5px]',
              isDuplicate && 'font-semibold text-status-red-ink',
              canEdit && 'cursor-text hover:bg-surface-hover'
            )}
            onDoubleClick={canEdit ? () => onStartEdit?.(field.key) : undefined}
            title={value ? `${field.label}: ${value}${canEdit ? ' — double-click to edit' : ''}` : undefined}
            data-device-cell={field.key}
          >
            {value || <span className="text-ink-4" aria-label={`No ${field.label.toLowerCase()}`}>—</span>}
          </span>
        );
      })}
    </>
  );
}

const CHANGE_TONE = { added: 'green', removed: 'red', renamed: 'blue', edited: 'blue', moved: 'violet', reordered: 'gray' };

/**
 * 08 #4: "Review 5 changes" in the save bar — every unsaved structure change in words (structureDiff), so the
 * cut-off summary is never the only place to read them.
 */
export function StructureChangesReview({ changes = [] }) {
  if (!changes.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" data-testid="structure-review-changes">
          <ListChecks aria-hidden="true" />
          Review {changes.length} change{changes.length === 1 ? '' : 's'}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-[400px] max-w-[calc(100vw-32px)] rounded-xl border-line bg-surface p-2 text-ink shadow-overlay">
        <p className="px-2 pb-1.5 pt-1 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3">Not saved yet</p>
        <ul className="max-h-[320px] space-y-0.5 overflow-y-auto" data-testid="structure-review-list">
          {changes.map((change, index) => (
            <li key={`${change.type}-${change.id || change.name}-${index}`} className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-[13px] leading-snug text-ink-2">
              <ToneDot tone={CHANGE_TONE[change.type] || 'gray'} className="mt-1.5" />
              <span className="min-w-0">{describeChange(change)}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** "Done" button shown at the end of a device row while it is being edited. */
export function StructureDeviceDoneButton({ onDone }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onDone} className="h-8">
      <Check aria-hidden="true" />
      Done
    </Button>
  );
}

/* ------------------------------------------------------------------ search (idea 5) */

const KIND_ICONS = { building: Building2, floor: Layers, sub: Server, room: DoorOpen, zone: SquareDashed, panel: Cpu, device: Cpu, 'sub-item': Cpu };
const KIND_LABELS = { building: 'Building', floor: 'Floor', sub: 'Sub', room: 'Room', zone: 'Zone', panel: 'Panel', device: 'Device', 'sub-item': 'Sub-item' };

/**
 * Search over the whole structure. Results list name, address / part number and the full path; choosing
 * one calls onOpen(entry) — the page opens its parents and scrolls to it.
 */
export function StructureSearch({ index = [], onOpen, className }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const listId = useId();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const found = useMemo(() => searchStructureIndex(index, query, { limit: 40 }), [index, query]);
  const hasQuery = Boolean(query.trim());
  const expanded = open && hasQuery;
  // The option that has focus (arrow keys move DOM focus into the list); -1 = the input.
  const [activeIndex, setActiveIndex] = useState(-1);
  useEffect(() => { setActiveIndex(-1); }, [query, open]);

  // Combobox pattern on the foundation SearchField's <input> (it takes no ARIA props; the ref reaches it).
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (expanded) input.setAttribute('aria-controls', listId);
    else input.removeAttribute('aria-controls');
  }, [expanded, listId]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  const choose = (entry) => {
    setOpen(false);
    onOpen?.(entry);
  };
  const focusOption = (position) => {
    const options = wrapperRef.current?.querySelectorAll('[data-structure-result]') || [];
    if (!options.length) return;
    const next = Math.max(0, Math.min(options.length - 1, position));
    setActiveIndex(next);
    options[next].focus();
  };
  const onOptionKey = (event, position) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); focusOption(position + 1); }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (position === 0) { setActiveIndex(-1); inputRef.current?.focus(); }
      else focusOption(position - 1);
    }
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); inputRef.current?.focus(); }
  };

  const first = found.results[0];
  const hint = !hasQuery
    ? <>Try an address like <span className={cn(MONO, 'rounded bg-surface-2 px-1 text-[11.5px] text-ink-2')}>1.1.12</span> or a part number</>
    : found.total
      ? <><b className="font-semibold text-ink-2">{formatNumber(found.total, { decimals: 0 })}</b> match{found.total === 1 ? '' : 'es'}{first ? ` · first in ${first.path[0] || first.name}` : ''}</>
      : <>No device, address or part number matches “{query.trim()}”</>;

  return (
    <div ref={wrapperRef} className={cn('relative w-full sm:w-[360px]', className)} data-testid="structure-search">
      <SearchField
        ref={inputRef}
        value={query}
        onChange={(next) => { setQuery(next); setOpen(Boolean(next.trim())); }}
        placeholder="Find device, address or part no."
        label="Find in the project structure"
        testId="structure-search-input"
        onlyWhenVisible
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && found.results.length) { event.preventDefault(); setOpen(true); window.requestAnimationFrame(() => focusOption(0)); }
          if (event.key === 'Enter' && first) { event.preventDefault(); choose(first); }
        }}
        className="w-full"
      />
      <p className="mt-1 truncate pl-1 text-[12px] text-ink-3" aria-live="polite">{hint}</p>
      {expanded ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Matches in the structure"
          className="absolute left-0 right-0 top-10 z-40 max-h-[360px] overflow-y-auto rounded-xl border border-line bg-surface p-1.5 shadow-overlay sm:w-[460px]"
          data-testid="structure-search-results"
        >
          {found.results.length ? found.results.map((entry, position) => {
            const Icon = KIND_ICONS[entry.kind] || Cpu;
            const code = entry.matchedOn === 'part' ? entry.partNumber : entry.address || entry.partNumber;
            return (
              <button
                key={entry.id}
                type="button"
                role="option"
                aria-selected={position === activeIndex}
                data-structure-result={entry.id}
                onFocus={() => setActiveIndex(position)}
                onClick={() => choose(entry)}
                onKeyDown={(event) => onOptionKey(event, position)}
                className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
              >
                <span className="ui-icon-tile tone-navy mt-0.5 h-7 w-7 shrink-0" aria-hidden="true"><Icon className="h-3.5 w-3.5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-[13px] font-semibold text-ink">{entry.name}</span>
                    {code ? <span className={cn(MONO, 'shrink-0 rounded bg-surface-2 px-1.5 text-[11.5px] text-ink-2')}>{code}</span> : null}
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-3">{KIND_LABELS[entry.kind] || entry.kind}</span>
                  </span>
                  <span className="block truncate text-[12px] text-ink-3">{entry.path.length ? entry.path.join(' › ') : 'Top level'}</span>
                </span>
              </button>
            );
          }) : (
            <div className="flex items-center gap-2 px-2.5 py-3 text-[13px] text-ink-3">
              <SearchX className="h-4 w-4" aria-hidden="true" />
              Nothing matches “{query.trim()}”.
            </div>
          )}
          {found.total > found.results.length ? (
            <p className="px-2.5 pb-1 pt-1.5 text-[12px] text-ink-3">Showing the first {found.results.length} of {formatNumber(found.total, { decimals: 0 })} — type more to narrow down.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
